/**
 * ColumnAnalysisPage — análisis de columnas (gravedad + sismo opcional).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Columns3, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import {
  Card,
  Divider,
  ForceItem,
  NumberField,
  Row,
  RowKV,
  Section,
  SelectField,
  WarningsCard,
} from '@/features/analysis/shared/primitives';
import { useColumnAnalysisStore } from '@/features/analysis/columns/store';
import { useColumnAnalysisQuery } from '@/features/analysis/columns/useColumnAnalysis';
import type {
  ColumnAnalysisRequest,
} from '@/features/analysis/columns/types';
import {
  SOIL_LABELS,
  SYSTEM_LABELS,
  USAGE_LABELS,
  USE_LABELS,
  ZONE_LABELS,
  type LiveLoadUse,
  type SoilProfile,
  type SystemKind,
  type UsageCategory,
  type ZoneCode,
} from '@/features/analysis/beams/types';

export function ColumnAnalysisPage() {
  const request = useColumnAnalysisStore((s) => s.request);
  const setGeometry = useColumnAnalysisStore((s) => s.setGeometry);
  const setAutoLoads = useColumnAnalysisStore((s) => s.setAutoLoads);
  const addFloor = useColumnAnalysisStore((s) => s.addFloor);
  const updateFloor = useColumnAnalysisStore((s) => s.updateFloor);
  const removeFloor = useColumnAnalysisStore((s) => s.removeFloor);
  const toggleSeismic = useColumnAnalysisStore((s) => s.toggleSeismic);
  const setSeismic = useColumnAnalysisStore((s) => s.setSeismic);
  const setMaterials = useColumnAnalysisStore((s) => s.setMaterials);
  const loadRequest = useColumnAnalysisStore((s) => s.loadRequest);

  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data) {
      loadRequest(designQuery.data.payload as unknown as ColumnAnalysisRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const { data, isFetching } = useColumnAnalysisQuery(request);
  const navigate = useNavigate();
  const [saveOpen, setSaveOpen] = useState(false);

  const onDesign = () => {
    if (!data) return;
    navigate('/app/columns/new', {
      state: {
        prefill: {
          source: 'analysis',
          analysisId: designId,
          section: {
            type: 'rectangular',
            L1: data.design_prefill.L1_cm,
            L2: data.design_prefill.L2_cm,
            H_clear: data.design_prefill.H_clear_cm,
            e1: 15,
            e2: 15,
          },
          materials: { fc: request.fc, fy: request.fy, cover: request.cover_cm, TMA: 1.905 },
          loads: {
            Pu: data.design_prefill.Pu_kgf,
            Mu_x: data.design_prefill.Mu_x_kgfcm,
            Mu_y: data.design_prefill.Mu_y_kgfcm,
            Vu_x: 0,
            Vu_y: 0,
          },
        },
      },
    });
  };

  return (
    <div className="flex h-full flex-col">
      <header
        className="flex h-14 shrink-0 items-center justify-between border-b px-6"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
      >
        <div>
          <div
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]"
            style={{ color: 'var(--text-muted)' }}
          >
            <Columns3 size={12} strokeWidth={1.8} /> Análisis E.020 + E.030 método estático
          </div>
          <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
            Análisis de columnas
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSaveOpen(true)}>
            <Save size={14} strokeWidth={1.8} /> {designId ? 'Actualizar' : 'Guardar'}
          </Button>
          <Button variant="primary" size="md" onClick={onDesign} disabled={!data || isFetching}>
            Diseñar columna <ArrowRight size={14} strokeWidth={2} />
          </Button>
        </div>
      </header>

      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="analysis_columns"
        payload={request as unknown as Record<string, unknown>}
        existing={designQuery.data ?? null}
      />

      <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[340px_minmax(0,1fr)]">
        <div
          className="overflow-y-auto border-r p-5 scrollbar-thin"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
        >
          <Section title="Geometría">
            <Row>
              <NumberField label="L1 (cm)" value={request.geometry.L1_cm} onChange={(v) => setGeometry({ L1_cm: v })} />
              <NumberField label="L2 (cm)" value={request.geometry.L2_cm} onChange={(v) => setGeometry({ L2_cm: v })} />
            </Row>
            <NumberField
              label="H libre (cm)"
              value={request.geometry.H_clear_cm}
              onChange={(v) => setGeometry({ H_clear_cm: v })}
            />
          </Section>

          <Section title="Área tributaria">
            <NumberField
              label="A_trib por piso (m²)"
              value={request.auto_loads.tributary_area_m2}
              onChange={(v) => setAutoLoads({ tributary_area_m2: v })}
              step={0.5}
            />
            <NumberField
              label="DL losa (kgf/m²)"
              value={request.auto_loads.slab_dead_load_kgf_m2}
              onChange={(v) => setAutoLoads({ slab_dead_load_kgf_m2: v })}
              step={50}
            />
          </Section>

          <Section title={`Pisos (${request.auto_loads.floors.length})`}>
            {request.auto_loads.floors.map((f, i) => (
              <div
                key={i}
                className="mb-2 rounded-[var(--radius-sm)] border p-2"
                style={{ borderColor: 'var(--border-default)', background: 'var(--surface-2)' }}
              >
                <Row>
                  <SelectField
                    label="Uso"
                    value={f.use}
                    options={USE_LABELS as Record<string, string>}
                    onChange={(v) => updateFloor(i, { use: v as LiveLoadUse })}
                  />
                  <NumberField
                    label="h (m)"
                    value={f.height_m}
                    onChange={(v) => updateFloor(i, { height_m: v })}
                    step={0.1}
                    compact
                  />
                </Row>
                <button
                  type="button"
                  onClick={() => removeFloor(i)}
                  className="mt-1 flex w-full items-center justify-center gap-1 rounded text-[11px]"
                  style={{ color: 'var(--danger)' }}
                >
                  <Trash2 size={11} /> Eliminar piso
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addFloor}
              className="flex w-full items-center justify-center gap-1 rounded-[var(--radius-sm)] border border-dashed py-2 text-[12px]"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
            >
              <Plus size={12} /> Agregar piso
            </button>
          </Section>

          <Section title="Sismo (opcional)">
            <label className="mb-2 flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={request.seismic !== null}
                onChange={(e) => toggleSeismic(e.currentTarget.checked)}
                className="h-3.5 w-3.5 accent-[var(--accent)]"
              />
              Considerar sismo E.030
            </label>
            {request.seismic && (
              <>
                <SelectField
                  label="Zona"
                  value={request.seismic.zone}
                  options={ZONE_LABELS as Record<string, string>}
                  onChange={(v) => setSeismic({ zone: v as ZoneCode })}
                />
                <SelectField
                  label="Categoría"
                  value={request.seismic.usage}
                  options={USAGE_LABELS as Record<string, string>}
                  onChange={(v) => setSeismic({ usage: v as UsageCategory })}
                />
                <SelectField
                  label="Suelo"
                  value={request.seismic.soil}
                  options={SOIL_LABELS as Record<string, string>}
                  onChange={(v) => setSeismic({ soil: v as SoilProfile })}
                />
                <SelectField
                  label="Sistema"
                  value={request.seismic.system}
                  options={SYSTEM_LABELS as Record<string, string>}
                  onChange={(v) => setSeismic({ system: v as SystemKind })}
                />
                <Row>
                  <NumberField
                    label="hn (m)"
                    value={request.seismic.building_height_m}
                    onChange={(v) => setSeismic({ building_height_m: v })}
                    step={0.5}
                  />
                  <NumberField
                    label="P_sis (kgf)"
                    value={request.seismic.seismic_weight_kgf}
                    onChange={(v) => setSeismic({ seismic_weight_kgf: v })}
                    step={1000}
                  />
                </Row>
                <NumberField
                  label="Frac. cortante col"
                  value={request.seismic.beam_shear_fraction}
                  onChange={(v) => setSeismic({ beam_shear_fraction: v })}
                  step={0.05}
                />
              </>
            )}
          </Section>

          <Section title="Materiales">
            <Row>
              <NumberField label="f'c" value={request.fc} onChange={(v) => setMaterials({ fc: v })} />
              <NumberField label="fy" value={request.fy} onChange={(v) => setMaterials({ fy: v })} />
            </Row>
          </Section>
        </div>

        <div className="overflow-y-auto p-6 scrollbar-thin">
          {data ? (
            <>
              <Card title="Cargas calculadas">
                <Row>
                  <div>
                    <RowKV k="P_D total" v={data.breakdown.P_D_total} unit="kgf" />
                    <RowKV k="P_L total" v={data.breakdown.P_L_total} unit="kgf" />
                    <RowKV k="PP columna" v={data.breakdown.PP_column} unit="kgf" />
                    <Divider />
                    <RowKV k="Pu gravedad" v={data.breakdown.Pu_gravity} unit="kgf" bold accent />
                  </div>
                  <div>
                    {data.seismic && (
                      <>
                        <RowKV k="C" v={data.seismic.C} />
                        <RowKV k="T edif" v={data.seismic.T} unit="s" />
                        <RowKV k="V_basal" v={data.seismic.V_basal_building} unit="kgf" />
                        <Divider />
                        <RowKV k="V columna" v={data.seismic.V_column} unit="kgf" bold />
                        <RowKV k="M columna" v={data.seismic.M_column / 100} unit="kgf·m" bold />
                      </>
                    )}
                  </div>
                </Row>
              </Card>

              <Card title="Envolvente">
                <div className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-4">
                  <ForceItem label="Pu máx" value={data.envelope.Pu_max} unit="kgf" />
                  <ForceItem label="Pu mín" value={data.envelope.Pu_min} unit="kgf" />
                  <ForceItem label="Mu_x" value={data.envelope.Mu_x_max / 100} unit="kgf·m" />
                  <ForceItem label="Mu_y" value={data.envelope.Mu_y_max / 100} unit="kgf·m" />
                </div>
                <div className="mt-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Combo gobernante: {data.envelope.governing_combo}
                </div>
              </Card>

              <WarningsCard items={data.warnings} />
            </>
          ) : (
            <div className="flex items-center justify-center py-20" style={{ color: 'var(--text-muted)' }}>
              <Loader2 size={20} className="mr-2 animate-spin" /> Calculando…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
